import { type RefObject, useEffect, useMemo, useState } from 'react'
import { type ViewportListRef } from 'react-viewport-list'
import { usePrevious } from 'react-use'
import { v4 as uuid } from 'uuid'
import Mousetrap from 'mousetrap'
import { proxy, useSnapshot } from 'valtio'

import {
  useActiveTrayCardIndex,
  useTray,
} from '../../../../../firebase/hooks/useTray'
import type { TrayCard as TrayCardType } from '../../../../../firebase/model/tray-cards/cards-model'

const useFilteredTrayCards = (filter: TrayCardType['type'][]) => {
  const { cardOrder, cards } = useTray()

  return useMemo(() => {
    return cardOrder
      .map<[idx: number, id: string]>((id, idx) => [idx, id])
      .filter(([_, id]) => {
        const c = cards.get(id)
        return c && filter.includes(c.type)
      })
  }, [cardOrder, cards, filter])
}

const useNearestToActiveIdx = (
  cardsList: (readonly [idx: number, id: string])[]
) => {
  const activeCardIndex = useActiveTrayCardIndex()

  return useMemo(() => {
    if (cardsList.length <= 0) return undefined
    if (cardsList.length <= 1) return 0

    for (let i = 0, bestDistance = Infinity; i < cardsList.length; i += 1) {
      const [idx] = cardsList[i]
      const distance = Math.abs(idx - activeCardIndex)

      // overshot
      if (distance > bestDistance) return i - 1

      bestDistance = distance
    }
    return cardsList.length - 1
  }, [activeCardIndex, cardsList])
}

const useAutoScrollToActive = (
  viewportListRef: RefObject<ViewportListRef>,
  cardsList: (readonly [idx: number, id: string])[]
) => {
  const nearestToActiveIdx = useNearestToActiveIdx(cardsList)
  const previousIdx = usePrevious(nearestToActiveIdx)

  useEffect(() => {
    if (nearestToActiveIdx === undefined) return
    if (nearestToActiveIdx === previousIdx) return

    if (nearestToActiveIdx !== -1) {
      viewportListRef.current?.scrollToIndex({
        index: nearestToActiveIdx,
        alignToTop: true,
        offset:
          nearestToActiveIdx > (previousIdx ?? 0)
            ? -(window.innerHeight * 0.1)
            : -(window.innerHeight * 0.25),
        prerender: 8,
        delay: 100,
      })
    }
  }, [nearestToActiveIdx, previousIdx, viewportListRef])
}

const registeredClicker = proxy({
  priority: -Infinity,
  identifier: '',
})

const useClickerKeys = (
  onBackward: () => void,
  onForward: () => void,
  priority: number = 0
) => {
  const [identifier] = useState(uuid())
  const currentRegister = useSnapshot(registeredClicker)

  useEffect(() => {
    if (registeredClicker.priority > priority) return undefined
    if (
      registeredClicker.priority === priority &&
      registeredClicker.identifier !== identifier
    )
      return undefined

    Mousetrap.bind(['down', 'right', 'pagedown'], (e) => {
      e.preventDefault()
      onForward()
    })
    Mousetrap.bind(['up', 'left', 'pageup'], (e) => {
      e.preventDefault()
      onBackward()
    })
    registeredClicker.priority = priority
    registeredClicker.identifier = identifier

    return () => {
      if (registeredClicker.identifier === identifier) {
        Mousetrap.unbind(['down', 'right', 'pagedown'])
        Mousetrap.unbind(['up', 'left', 'pageup'])
        registeredClicker.priority = -Infinity
        registeredClicker.identifier = ''
      }
    }
  }, [onBackward, onForward, currentRegister.identifier, priority, identifier])
}

export {
  useFilteredTrayCards,
  useNearestToActiveIdx,
  useAutoScrollToActive,
  useClickerKeys,
}
